// Festival-specific images, sourced from Wikimedia Commons (freely licensed,
// stable hotlink via Special:FilePath — resolves to the actual media file).
// Matched by a case-insensitive substring of the festival's englishName.
// Only entries we're confident actually depict that specific festival are
// listed here; anything unmatched falls back to a themed emoji instead of a
// generic/unrelated photo (see getFestivalImage below).
//
// Order matters: more specific multi-word keys (e.g. "mattu pongal") must
// come before shorter keys they contain (e.g. "pongal"), since matching
// walks this object in insertion order and takes the first hit.
const FESTIVAL_IMAGE_MAP: Record<string, string> = {
    "mattu pongal": "https://commons.wikimedia.org/wiki/Special:FilePath/Maattu_Pongal_-_kids_in_tractors.jpg",
    pongal: "https://commons.wikimedia.org/wiki/Special:FilePath/Pot_breaking_celebrations_with_human_pyramid_Pongal_Hindu_festival.jpg",
    bogi: "https://commons.wikimedia.org/wiki/Special:FilePath/Large_bonfire.jpg",
    "thiruvalluvar day": "https://commons.wikimedia.org/wiki/Special:FilePath/Thiruvalluvar_Statue_at_Kanyakumari_01.jpg",
    "republic day": "https://commons.wikimedia.org/wiki/Special:FilePath/Karnataka_Tableau_Republic_Day_Parade_2025.jpg",
    "independence day": "https://commons.wikimedia.org/wiki/Special:FilePath/Indian_Independence_Day_at_the_Red_Fort.jpg",
    "gandhi jayanthi": "https://commons.wikimedia.org/wiki/Special:FilePath/Portrait_Gandhi.jpg",
    "children's day": "https://commons.wikimedia.org/wiki/Special:FilePath/Stamp_of_India_-_1998_-_Colnect_161885_-_Childrens_Day.jpeg",
    "good friday": "https://commons.wikimedia.org/wiki/Special:FilePath/A_Good_Friday_procession.JPG",
    christmas: "https://commons.wikimedia.org/wiki/Special:FilePath/Christmas_Tree_and_Presents.jpg",
    "may day": "https://commons.wikimedia.org/wiki/Special:FilePath/May_Day_-_Workers_Strike_2017_(34789591643).jpg",
    "maha shivaratri": "https://commons.wikimedia.org/wiki/Special:FilePath/Brihadisvara_Temple_during_Maha_Shivaratri-WUS03611_(edit).jpg",
    holi: "https://commons.wikimedia.org/wiki/Special:FilePath/Holi_Festival_of_Colors_Utah,_United_States_2013.jpg",
    "rama navami": "https://commons.wikimedia.org/wiki/Special:FilePath/Sri_Rama_Navami_Edurukolu_At_Bhadrachalam_Temple_04.jpg",
    deepavali: "https://commons.wikimedia.org/wiki/Special:FilePath/Diwali_Diyas_Lamps.jpg",
    diwali: "https://commons.wikimedia.org/wiki/Special:FilePath/Diwali_Diyas_Lamps.jpg",
    "vinayagar chaturthi": "https://commons.wikimedia.org/wiki/Special:FilePath/Matti_vinayaka_clay_ganesh_idol.jpg",
    "ganesh chaturthi": "https://commons.wikimedia.org/wiki/Special:FilePath/Matti_vinayaka_clay_ganesh_idol.jpg",
    "tamil new year": "https://commons.wikimedia.org/wiki/Special:FilePath/Poonga_Illam_Tamil_new_year_2014_kollam.JPG",
    puthandu: "https://commons.wikimedia.org/wiki/Special:FilePath/Poonga_Illam_Tamil_new_year_2014_kollam.JPG",
    navaratri: "https://commons.wikimedia.org/wiki/Special:FilePath/A_baby_girl_dressed_up_like_Godess_Durga_in_Navratri.jpg",
    "vijaya dasami": "https://commons.wikimedia.org/wiki/Special:FilePath/A_baby_girl_dressed_up_like_Godess_Durga_in_Navratri.jpg",
    "karthigai": "https://commons.wikimedia.org/wiki/Special:FilePath/Thiruvannamalai_Maha_Karthigai_Deepam_(5200919082).jpg",
    "vaikunta ekadashi": "https://commons.wikimedia.org/wiki/Special:FilePath/Vaikunta_perumal_temple,_Kanchipuram.JPG",
    "arudra darshan": "https://commons.wikimedia.org/wiki/Special:FilePath/Thillai_Nataraja_Temple,_Chidambaram.jpg",
    "aarudra darisanam": "https://commons.wikimedia.org/wiki/Special:FilePath/Thillai_Nataraja_Temple,_Chidambaram.jpg",
};

// Emoji fallback per category, used whenever the festival name doesn't match
// a specific photo above — themed, but not a misleading/unrelated picture.
const CATEGORY_EMOJI: Record<string, string> = {
    Holiday: "🎉",
    "Tamil Festival": "🪔",
    Muhurtham: "🌙",
};

export type FestivalImage = { type: "photo"; url: string } | { type: "emoji"; value: string };

export function getFestivalImage(englishName: string, category?: string): FestivalImage {
    const lower = (englishName || "").toLowerCase();
    const matchKey = Object.keys(FESTIVAL_IMAGE_MAP).find((key) => lower.includes(key));
    if (matchKey) {
        // Special:FilePath redirects to the full-resolution original by
        // default (some of these are multi-MB) — ?width= asks MediaWiki to
        // redirect to a pre-generated thumbnail instead, which is what was
        // making these so slow to load into a 48px thumbnail slot.
        return { type: "photo", url: `${FESTIVAL_IMAGE_MAP[matchKey]}?width=200` };
    }
    return { type: "emoji", value: CATEGORY_EMOJI[category || ""] || "🎊" };
}
